/** Placeholder for a value that is missing or not yet loaded. */
export const MISSING = "—";

const present = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

export function formatNumber(value: number | null | undefined, digits = 1): string {
  return present(value) ? value.toFixed(digits) : MISSING;
}

/** Fixed decimals with thousands separators: 1705.3 -> "1,705". */
export function formatGrouped(value: number | null | undefined, digits = 0): string {
  if (!present(value)) return MISSING;
  return value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Milliseconds: whole numbers once the decimals stop mattering (78.5, but 1,095). */
export function formatMs(value: number | null | undefined): string {
  if (!present(value)) return MISSING;
  return value >= 100 ? formatGrouped(value, 0) : value.toFixed(1);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  return present(value) ? `${value.toFixed(digits)}%` : MISSING;
}

/** Availability never rounds up to 100%: a single failed probe must stay visible. */
export function formatAvailability(value: number | null | undefined): string {
  if (!present(value)) return MISSING;
  if (value >= 100) return "100%";
  const digits = value >= 99.9 ? 2 : 1;
  const factor = 10 ** digits;
  const rounded = value.toFixed(digits);
  return `${Number(rounded) >= 100 ? (Math.floor(value * factor) / factor).toFixed(digits) : rounded}%`;
}

export interface Quantity {
  value: string;
  unit: string;
}

/** A link rate given in kbit/s, split so the unit can be set smaller than the figure. */
export function rateParts(value: number | null | undefined): Quantity {
  if (!present(value)) return { value: MISSING, unit: "" };
  if (value >= 1000) return { value: (value / 1000).toFixed(value >= 100_000 ? 0 : 1), unit: "Mbit/s" };
  return { value: value.toFixed(0), unit: "kbit/s" };
}

export function formatRate(value: number | null | undefined): string {
  const parts = rateParts(value);
  return parts.unit ? `${parts.value} ${parts.unit}` : parts.value;
}

export function formatBytes(value: number | null | undefined): string {
  if (!present(value)) return MISSING;
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const index = Math.min(Math.floor(Math.log10(Math.max(value, 1)) / 3), units.length - 1);
  const scaled = value / 1000 ** index;
  const digits = scaled >= 100 || index === 0 ? 0 : scaled >= 10 ? 1 : 2;
  return `${scaled.toFixed(digits)} ${units[index]}`;
}

/** Binary gigabytes, the unit vnStat and the KiwiVM panel both count in. */
export function formatGib(bytes: number | null | undefined, digits = 2): string {
  return present(bytes) ? formatGrouped(bytes / 2 ** 30, digits) : MISSING;
}

/** Traffic volume at whatever binary unit keeps it readable. */
export function formatVolume(bytes: number | null | undefined): string {
  if (!present(bytes)) return MISSING;
  if (bytes >= 2 ** 30) return `${formatGrouped(bytes / 2 ** 30, 2)} GiB`;
  if (bytes >= 2 ** 20) return `${formatGrouped(bytes / 2 ** 20, 1)} MiB`;
  return `${formatGrouped(bytes / 2 ** 10, 0)} KiB`;
}

export function formatUptime(seconds: number | null | undefined): string {
  if (!present(seconds)) return MISSING;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return days > 0 ? `${days} 天 ${hours} 小时` : `${hours} 小时`;
}

export function formatTimestamp(timestamp: number | null, timezone: string): string {
  if (!timestamp) return "尚未采集";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: timezone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(timestamp * 1000);
}

export function formatAge(age: number | null): string {
  if (age == null) return "等待首次采集";
  if (age < 10) return "刚刚更新";
  if (age < 60) return `${age} 秒前更新`;
  return `${Math.floor(age / 60)} 分钟前更新`;
}

const pad = (value: number) => String(value).padStart(2, "0");

/** Wall-clock time in the viewer's own timezone, e.g. "19:06:13". */
export function formatClock(time: number, seconds = true): string {
  const date = new Date(time);
  const base = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return seconds ? `${base}:${pad(date.getSeconds())}` : base;
}

/** Label for one chart bucket: a date for daily buckets, date and time otherwise. */
export function formatBucketTime(time: number, stepMs: number): string {
  const date = new Date(time);
  const day = `${date.getMonth() + 1}月${date.getDate()}日`;
  if (stepMs >= 86_400_000) return day;
  return `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export interface StationTime {
  /** Hour of day at the observation point, 0-23. */
  hour: number;
  /** e.g. "周三 19:06". */
  label: string;
}

/** Local time at the observation point; falls back to the viewer's zone if the name is invalid. */
export function stationTime(time: number, timezone: string): StationTime {
  const read = (zone: string | undefined): StationTime => {
    const parts = new Intl.DateTimeFormat("zh-CN", {
      timeZone: zone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(time);
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    return { hour: Number(get("hour")) % 24, label: `${get("weekday")} ${get("hour")}:${get("minute")}` };
  };
  try {
    return read(timezone);
  } catch {
    return read(undefined);
  }
}
